/**
 * API Client for Device Commands
 * Handles all API calls for device control functionality
 */
import api from './api';

export interface CommandResponse {
  commandId: number;
  status: string;
  issuedAt: string;
}

export interface CommandStatus {
  id: number;
  companyId: number;
  deviceId: number;
  command: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  issuedBy: string;
  issuedAt: string;
  startedAt?: string;
  completedAt?: string;
  result?: string;
  resultImage?: string; // Base64 encoded
  exitCode?: number;
}

export interface TerminalSessionResponse {
  sessionId: number;
  status: string;
  createdAt: string;
}

export interface TerminalCommandResponse {
  commandId: number;
  status: string;
  issuedAt: string;
}

export interface TerminalCommandStatus {
  id: number;
  command: string;
  status: 'pending' | 'running' | 'completed';
  issuedAt: string;
  completedAt?: string;
  output?: string;
  exitCode?: number;
}

class DeviceCommandsApi {
  /**
   * Issue a quick action command to a device
   */
  async issueCommand(deviceId: number, command: string): Promise<CommandResponse> {
    const response = await api.post(`/devices/${deviceId}/command`, { command });
    return response.data;
  }

  /**
   * Get command status by ID (for polling)
   */
  async getCommandStatus(commandId: number): Promise<CommandStatus> {
    const response = await api.get(`/commands/${commandId}`);
    return response.data;
  }

  /**
   * Get command history for a device
   */
  async getCommandHistory(deviceId: number, limit: number = 50): Promise<CommandStatus[]> {
    const response = await api.get(`/devices/${deviceId}/commands?limit=${limit}`);
    return response.data;
  }

  /**
   * Cancel a pending command
   */
  async cancelCommand(commandId: number): Promise<void> {
    await api.delete(`/commands/${commandId}`);
  }

  /**
   * Create a terminal session for a device
   */
  async createTerminalSession(deviceId: number): Promise<TerminalSessionResponse> {
    const response = await api.post(`/devices/${deviceId}/terminal/session`);
    return response.data;
  }

  /**
   * Send a command to a terminal session
   */
  async sendTerminalCommand(
    sessionId: number,
    command: string
  ): Promise<TerminalCommandResponse> {
    const response = await api.post(`/terminal/sessions/${sessionId}/command`, { command });
    return response.data;
  }

  /**
   * Get all commands for a terminal session
   */
  async getTerminalCommands(sessionId: number): Promise<TerminalCommandStatus[]> {
    const response = await api.get(`/terminal/sessions/${sessionId}/commands`);
    return response.data.commands;
  }

  /**
   * Close a terminal session
   */
  async closeTerminalSession(sessionId: number): Promise<void> {
    await api.delete(`/terminal/sessions/${sessionId}`);
  }

  /**
   * Poll for command completion
   * Returns a promise that resolves when command is complete
   *
   * Waits four minutes by default, against the device's three-minute limit for
   * ordinary commands. It has to outlast the device rather than match it: if
   * both give up at the same moment, a command that genuinely timed out is
   * reported here as "polling timed out" - a message about this browser -
   * instead of the device's own account, which is the one worth reading.
   *
   * Installing software has no limit on the device, so callers doing that pass
   * their own budget. Giving up here does not stop anything: the work is on the
   * Pi, and reopening the panel picks the same command back up by its id.
   *
   * onProgress is called with each intermediate status. The device reports how
   * long it has been going every thirty seconds, and passing that through is
   * what turns a ten minute wait from an unexplained spinner into something a
   * person is willing to leave alone.
   *
   * signal stops the watching, not the command: a panel closed halfway through
   * an install should not go on asking about it for the next half hour.
   */
  async pollCommandCompletion(
    commandId: number,
    maxAttempts: number = 240,
    intervalMs: number = 1000,
    onProgress?: (status: CommandStatus) => void,
    signal?: AbortSignal
  ): Promise<CommandStatus> {
    for (let i = 0; i < maxAttempts; i++) {
      if (signal?.aborted) throw new DOMException('Stopped watching the command', 'AbortError');

      const status = await this.getCommandStatus(commandId);

      // Cancelled is as finished as completed: a command cancelled while it
      // waited for the device will never be picked up.
      if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
        return status;
      }

      if (onProgress) onProgress(status);

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error('Command polling timed out');
  }
}

// Export singleton instance
export const deviceCommandsApi = new DeviceCommandsApi();
